import {
  persistNativePrimarySession,
  reconcileBiometricUser,
} from "@features/BiometricAuthentication/biometricUnlock";
import { t } from "@lingui/macro";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { Platform } from "react-native";

import { useFlags } from "@/utils/launchDarkly";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  clearNativeSessionSignedOut,
  resumeNativeSessionPersistence,
} from "@whoosh/relay/src";

import { ProceedToVerifyCodeStepAtom } from "../../../../../atoms/actions";
import { VerificationModalCompleteActionAtom } from "../../../../../atoms/stores";
import { CreateCustomerUserInput } from "../../../../../hooks/__generated__/useCreateNewCustomerUserMutation.graphql";
import { useCreateEmailVerification } from "../../../../../hooks/useCreateEmailVerification";
import { useCreateNewCustomerUser } from "../../../../../hooks/useCreateNewCustomerUser";
import { useCreatePhoneVerification } from "../../../../../hooks/useCreatePhoneVerification";

type AssignmentTokenContact = "email" | "phone";

export const AssignmentTokenRecoveryContactAtom =
  atom<AssignmentTokenContact | null>(null);

export const getAssignmentTokenContactToReverify = (
  input: CreateCustomerUserInput,
  lastRecoveredContact: AssignmentTokenContact | null
) => {
  const contactsWithTokens: AssignmentTokenContact[] = [];

  if (input.email && input.emailAssignmentToken) {
    contactsWithTokens.push("email");
  }

  if (input.phoneNumber && input.phoneAssignmentToken) {
    contactsWithTokens.push("phone");
  }

  return (
    contactsWithTokens.find((contact) => contact !== lastRecoveredContact) ||
    contactsWithTokens[0] ||
    null
  );
};

export const shouldRetryAccountCreationAfterVerification = ({
  hasAssignmentToken,
  hasAuthenticatedUser,
  hasFormDataToSubmit,
  verificationType,
}: {
  hasAssignmentToken: boolean;
  hasAuthenticatedUser: boolean;
  hasFormDataToSubmit: boolean;
  verificationType: null | string | undefined;
}) =>
  !hasAuthenticatedUser &&
  hasAssignmentToken &&
  hasFormDataToSubmit &&
  verificationType === "CreatingAccountAndVerifyingOtherContact";

export const useTryToCreateAccount = (hasAuthenticatedUser = false) => {
  const { biometricAuth } = useFlags();
  const completeAction = useAtomValue(VerificationModalCompleteActionAtom);
  const lastRecoveredContact = useAtomValue(AssignmentTokenRecoveryContactAtom);
  const setLastRecoveredContact = useSetAtom(
    AssignmentTokenRecoveryContactAtom
  );
  const [createUser, isCreating] = useCreateNewCustomerUser();
  const [createEmailVerification] = useCreateEmailVerification();
  const [createPhoneVerification] = useCreatePhoneVerification();
  const proceedToVerifyCode = useSetAtom(ProceedToVerifyCodeStepAtom);

  const { showToast } = useToast();

  const startContactVerification = (
    contact: AssignmentTokenContact,
    input: CreateCustomerUserInput
  ) => {
    if (contact === "email" && input.email) {
      createEmailVerification(input.email, () => {
        setLastRecoveredContact(contact);
        proceedToVerifyCode({
          email: input.email,
          phone: null,
          type: "CreatingAccountAndVerifyingOtherContact",
          formDataToSubmit: input,
        });
      });
      return true;
    }

    if (contact === "phone" && input.phoneNumber) {
      createPhoneVerification(input.phoneNumber, () => {
        setLastRecoveredContact(contact);
        proceedToVerifyCode({
          email: null,
          phone: input.phoneNumber,
          type: "CreatingAccountAndVerifyingOtherContact",
          formDataToSubmit: input,
        });
      });
      return true;
    }

    return false;
  };

  const tryToCreateAccount = (input: CreateCustomerUserInput) => {
    if (hasAuthenticatedUser) {
      setLastRecoveredContact(null);
      completeAction?.();
      return true;
    }

    if (Platform.OS !== "web") {
      resumeNativeSessionPersistence();
    }

    createUser(
      input,
      async (res) => {
        if (Platform.OS !== "web") {
          const userId = res.responsePayload?.session?.user?.id;
          if (userId && biometricAuth === true) {
            await reconcileBiometricUser(userId);
          }
          await persistNativePrimarySession();
          await clearNativeSessionSignedOut();
        }

        setLastRecoveredContact(null);
        completeAction?.();
      },
      (res) => {
        const errorCodes = res.responsePayload?.errors
          .map((e) => (e?.code ? e.code : null))
          .filter((item) => !!item);

        const errorFields = res.responsePayload?.errors
          .flatMap((e) => (e?.field ? e.field : null))
          .filter((item) => !!item);

        const phoneNumberIsTaken =
          errorCodes?.includes(hasAlreadyBeenTaken) &&
          errorFields &&
          errorFields?.includes(phoneNumberIdField);

        if (errorCodes?.includes(invalidAssignmentTokenError)) {
          const contactToReverify = getAssignmentTokenContactToReverify(
            input,
            lastRecoveredContact
          );

          if (
            contactToReverify &&
            startContactVerification(contactToReverify, input)
          ) {
            return;
          }
        }

        if (errorCodes?.includes(matchingEmailError) && input.email) {
          /**
           * We have found a customer with the same email that is not yet verified --
           * so we need to go verify this email
           */
          startContactVerification("email", input);
        } else if (
          (errorCodes?.includes(matchingPhoneError) && input.phoneNumber) ||
          phoneNumberIsTaken
        ) {
          /**
           * We have found a customer with the same phone that is not yet verified --
           * so we need to go verify this phone
           */
          startContactVerification("phone", input);
        } else {
          showToast({
            description: t`Unable to create new user. ${
              res.responsePayload?.errors
                ?.map((e) => (e?.message ? e.message : ""))
                .join(", ") ?? ""
            }`,
            semanticStyle: "error",
            toastId: "create-customer-user-error",
          });
        }
      }
    );
    return true;
  };
  return [tryToCreateAccount, isCreating] as const;
};

const matchingEmailError = "matching_email_not_verified";
const matchingPhoneError = "matching_phone_not_verified";
const invalidAssignmentTokenError = "invalid_assignment_token";

const hasAlreadyBeenTaken = "has_already_been_taken";

const phoneNumberIdField = "phone_number_id";
